import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { FinishWebAuthnAuthenticationResponse } from '@cash-web/protos/squareup/cash/janus/api/WebAuthn.pb'
import logger from '@cash-web/shared/util-logger'
import { useMutationFinishWebAuthnAuthentication } from '@cash-web/shared-data-access-api/react-query/useMutationFinishWebAuthnAuthentication'
import { useMutationStartWebAuthnAuthentication } from '@cash-web/shared-data-access-api/react-query/useMutationStartWebAuthnAuthentication'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { createSingleUseFlowToken } from '@cash-web/shared-util-fetch'
import { WebResponseError, WebResponseSuccess } from '@squareup/customer-data-events-web'

import { getHeadersForWebAuthn } from '../getHeadersForWebAuthn'
import { signalUnknownPasskey } from '../passkeySignaling'
import { createCredential } from './createCredential'
import { decodeOptions } from './decodeOptions'
import { encodeCredential } from './encodeCredential'
import { getCredentialErrorCode, UNRECOGNIZED_CREDENTIAL_ERROR_CODE } from './getCredentialErrorCode'

const isRiskBlocked = (response?: { status: number; body: unknown }) =>
  response?.status === 403 && response.body === 'RISK_BLOCKED'

export type PasskeyErrorReason =
  | 'user-initiated'
  | 'risk-blocked'
  | 'unsupported-browser'
  | 'deleted-passkey'
  | 'unknown'

const getStartErrorReason = (error: {
  name: string
  response?: { status: number; body: unknown }
}): PasskeyErrorReason => {
  if (error.name === 'NotAllowedError') return 'user-initiated'
  if (error.name === 'NotSupportedError') return 'unsupported-browser'
  if (isRiskBlocked(error.response)) return 'risk-blocked'
  return 'unknown'
}

export function useAuthenticatePasskey({
  onSuccess,
  onError,
  onShowPasskeyDialog,
}: {
  onSuccess?: (response: FinishWebAuthnAuthenticationResponse, flowToken: string) => void
  onError?: (reason: PasskeyErrorReason) => void
  onShowPasskeyDialog?: () => void
}) {
  const { t } = useTranslation()
  const logCdfEvent = useLogCashWebCdfEvent()
  const [flowToken] = useState(createSingleUseFlowToken())

  const defaultErrorMsg = t('An error occurred while authenticating your passkey.', {
    context: 'error',
  })

  const finishWebAuthnAuthenticationApiNameForLogging = 'finish-webauthn-authentication'
  const {
    mutate: finishWebAuthnAuthentication,
    isLoading: isFinishLoading,
    error: finishError,
  } = useMutationFinishWebAuthnAuthentication({
    options: { optionalRequestHeaders: getHeadersForWebAuthn(), flowToken },
    mutationOptions: {
      onSuccess: async response => {
        logCdfEvent(WebResponseSuccess({ apiName: finishWebAuthnAuthenticationApiNameForLogging }))
        onSuccess?.(response, flowToken)
      },
      onError: e => {
        logCdfEvent(
          WebResponseError({
            apiName: finishWebAuthnAuthenticationApiNameForLogging,
            httpStatusCode: e.response?.status,
          })
        )
      },
    },
  })

  const startWebAuthnAuthenticationApiNameForLogging = 'start-webauthn-authentication'
  const getCredentialApiNameForLogging = 'navigator.credentials.get'
  const {
    mutate: startWebAuthnAuthentication,
    isLoading: isStartLoading,
    error: startError,
  } = useMutationStartWebAuthnAuthentication({
    options: { optionalRequestHeaders: getHeadersForWebAuthn(), flowToken },
    mutationOptions: {
      onSuccess: async response => {
        logCdfEvent(WebResponseSuccess({ apiName: startWebAuthnAuthenticationApiNameForLogging }))

        const requestId = response.request_id

        // The server omits options when passkey login is unavailable.
        if (!response.options) {
          throw new Error('Passkey authentication options are unavailable')
        }

        const decodedOptions = decodeOptions(response.options)

        // The passkey system dialog will show when `createCredential` is called.
        onShowPasskeyDialog?.()

        try {
          const credential = await createCredential(decodedOptions)
          if (!credential) {
            onError?.('user-initiated')
            return
          }
          logCdfEvent(WebResponseSuccess({ apiName: getCredentialApiNameForLogging }))

          const encodedCredential = JSON.stringify(encodeCredential(credential))
          finishWebAuthnAuthentication(
            { request_id: requestId, credential: encodedCredential },
            {
              onError: e => {
                if (e.response?.status === 404) {
                  signalUnknownPasskey(credential.id, decodedOptions.rpId)
                  onError?.('deleted-passkey')
                } else {
                  onError?.(isRiskBlocked(e.response) ? 'risk-blocked' : 'unknown')
                }
              },
            }
          )
        } catch (e) {
          const errorCode = getCredentialErrorCode(e)
          logCdfEvent(WebResponseError({ apiName: getCredentialApiNameForLogging, httpStatusCode: errorCode }))
          if (errorCode === UNRECOGNIZED_CREDENTIAL_ERROR_CODE) {
            logger.error(e instanceof Error ? e : new Error(`Unrecognized passkey credential error: ${String(e)}`))
          }
          throw e
        }
      },
      onError: e => {
        logCdfEvent(
          WebResponseError({
            apiName: startWebAuthnAuthenticationApiNameForLogging,
            httpStatusCode: e.response?.status,
          })
        )
        onError?.(getStartErrorReason(e))
      },
    },
  })

  return {
    authenticatePasskey: () => startWebAuthnAuthentication({}),
    isLoading: isStartLoading || isFinishLoading,
    error: startError || finishError ? defaultErrorMsg : undefined,
  }
}
